<template>
  <div v-if="isShowAdultPlaceholder" :class="$style.placeholder">
    <BaseIcon name="exclamation-triangle" :class="$style.placeholderIcon" />
    <div>{{ $t('blogs.adultPlaceholder') }}</div>
  </div>
  <div v-else-if="activeBlog">
    <tabs
      :tabs="tabs"
      :currentTab="currentLanguage"
      :class="$style.tabs"
      @onClick="setCurrentLanguage"
    />
    <router-link
      v-for="post in posts" :key="post.uuid"
      :to="{ name: 'app-blogs-post', params: { blogId, postId: post.uuid } }"
      :class="$style.link"
    >
      <BlogPostListItem
        :post="post"
        :class="$style.post"
      />
    </router-link>
    <infinite-loading
      :identifier="infiniteId"
      @infinite="infiniteHandler"
      :class="$style.infiniteLoading"
    >
      <Spinner slot="spinner" />
      <div slot="no-more"></div>
      <div slot="no-results"></div>
    </infinite-loading>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import InfiniteLoading from 'vue-infinite-loading'
import Spinner from '@/components/Spinner'
import BlogPostListItem from './BlogPostListItem'
import Tabs from 'vue-tabs-with-active-line'
import { capitalize } from 'lodash'

const OFFSET = 20

export default {
  name: 'blog-post-list',
  props: {
    blogId: [String, null]
  },
  components: {
    InfiniteLoading,
    Spinner,
    BlogPostListItem,
    Tabs
  },
  data () {
    return {
      infiniteId: +new Date(),
      page: 0,
      currentLanguage: 'en'
    }
  },
  async created () {
    this.setDefaultLanguage()
    await this.fetchBlog(this.blogId)
    this.setDefaultLanguage()
  },
  computed: {
    ...mapGetters({
      blogById: 'entities/blogs/getBlog',
      postsByBlog: 'entities/posts/postsByBlog'
    }),
    activeBlogId () {
      return this.blogId // this.$route.query.id
    },
    activeBlog () {
      return this.blogById(this.activeBlogId)
    },
    posts () {
      if (!this.activeBlog) return []
      return this.postsByBlog(this.activeBlog.uuid).filter(post => post.language === this.currentLanguage)
    },
    isShowAdultPlaceholder () {
      if (!this.activeBlog) return
      const { isAdult, isOwn, isSubscribed } = this.activeBlog
      return isAdult && !isOwn && !isSubscribed
    },
    tabs () {
      if (!this.activeBlog) return []
      return this.activeBlog.blogInfo.map(obj => ({
        title: capitalize(obj.language),
        value: obj.language
      }))
    }
  },
  watch: {
    'activeBlog.uuid': {
      immediate: true,
      handler (uuid) {
        this.page = 0
        this.infiniteId += 1
      }
    }
  },
  methods: {
    ...mapActions({
      fetchBlog: 'entities/blogs/fetchByShortId',
      getBlogPosts: 'entities/posts/fetchBlogPosts'
    }),
    async infiniteHandler ($state) {
      if (!this.activeBlog) return
      const posts = await this.getBlogPosts({
        blogUuid: this.activeBlog.uuid,
        limit: OFFSET,
        offset: OFFSET * this.page
      })
      if (posts.length < OFFSET) {
        $state.complete()
      } else {
        $state.loaded()
        this.page += 1
      }
    },
    setCurrentLanguage (newTab) {
      this.currentLanguage = newTab
    },
    setDefaultLanguage () {
      if (this.activeBlog && this.activeBlog.isOwn) {
        this.setCurrentLanguage(this.activeBlog.defaultLanguage)
      }
    }
  }
}
</script>

<style module lang="scss">
.placeholder {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  font-size: 16px;
  color: $text-mid;
  position: relative;
  top: calc(50% - 34px);
  &Icon {
    font-size: 40px;
    margin-bottom: 10px;
  }
}
.link:not(:last-child) .post {
  border-bottom: 1px solid $border-color;
}
.infiniteLoading {
  height: 50px;
  padding: 20px 0;
}
.tabs:global(.tabs) {
  margin-bottom: 12px;
  :global(.tabs__item) {
    flex-grow: unset;
    flex-basis: auto;
  }
}
</style>
